import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createTableHook, tableFeatures } from "@tanstack/react-table";
import * as stylex from "@stylexjs/stylex";
import { queries, errorMessage, isUnauthorized } from "../lib/api";
import { styles } from "../lib/styles";
import type { Recording } from "../lib/schemas";
import { NavIcon, PageHeader, SearchField, Pagination } from "../components/Workspace";

export const Route = createFileRoute("/recordings/")({ component: RecordingsPage });
const { useAppTable, createAppColumnHelper } = createTableHook({ features: tableFeatures({}) });
const helper = createAppColumnHelper<Recording>();
function RecordingsPage() {
  const me = useQuery(queries.me());
  const list = useQuery({ ...queries.recordings(), enabled: me.data?.admin === true });
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const columns = useMemo(
    () => [
      helper.display({
        id: "when",
        header: "Started",
        cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
      }),
      helper.display({
        id: "server",
        header: "Server / account",
        cell: ({ row }) => `${row.original.alias} / ${row.original.login}`,
      }),
      helper.display({ id: "who", header: "Person", cell: ({ row }) => row.original.email }),
      helper.display({ id: "kind", header: "Channel", cell: ({ row }) => row.original.kind }),
      helper.display({
        id: "state",
        header: "State",
        cell: ({ row }) => (
          <span
            {...stylex.props(styles.status, row.original.status === "complete" && styles.applied)}
          >
            {row.original.status}
          </span>
        ),
      }),
      helper.display({
        id: "action",
        header: "Review",
        cell: ({ row }) =>
          row.original.status === "recording" ? (
            <span {...stylex.props(styles.small)}>In progress</span>
          ) : (
            <Link
              className="action-link"
              to="/recordings/$recordingId"
              params={{ recordingId: row.original.id }}
            >
              Replay <NavIcon name="ArrowRight" />
            </Link>
          ),
      }),
    ],
    [],
  );
  const data = (list.data?.items ?? []).filter(
    (item) =>
      (status === "all" || item.status === status) &&
      `${item.alias} ${item.email} ${item.login} ${item.kind}`
        .toLowerCase()
        .includes(filter.toLowerCase()),
  );
  const currentPage = Math.min(page, Math.max(0, Math.ceil(data.length / 15) - 1));
  const table = useAppTable({
    columns,
    data: data.slice(currentPage * 15, (currentPage + 1) * 15),
  });
  if (!me.data?.admin)
    return (
      <p role="alert" {...stylex.props(styles.error)}>
        Administrator access is required.
      </p>
    );
  return (
    <>
      <PageHeader
        title="Session recordings"
        description="Replay managed SSH output. Encrypted recordings are retained for 30 days."
      />
      {list.data && !list.data.storage.healthy && (
        <p role="alert" {...stylex.props(styles.error)}>
          Recording storage needs attention. New managed SSH access is blocked until it is healthy.
        </p>
      )}
      {list.error && (
        <div role="alert" {...stylex.props(styles.error)}>
          {errorMessage(list.error)}
          {isUnauthorized(list.error) && (
            <>
              {" "}
              <a href="/auth/login">Sign in again</a>
            </>
          )}
        </div>
      )}
      <section data-workspace-panel {...stylex.props(styles.panel)}>
        <div className="table-toolbar">
          <SearchField
            label="Search recordings"
            value={filter}
            onChange={(value) => {
              setFilter(value);
              setPage(0);
            }}
            placeholder="Search server, person or account…"
          />
          <label {...stylex.props(styles.field)}>
            State
            <select
              {...stylex.props(styles.select)}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(0);
              }}
            >
              <option value="all">All recordings</option>
              <option value="complete">Complete</option>
              <option value="recording">In progress</option>
              <option value="interrupted">Interrupted</option>
              <option value="failed">Failed</option>
            </select>
          </label>
        </div>
        <div {...stylex.props(styles.tableWrap)}>
          <table {...stylex.props(styles.table)}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => (
                    <th key={header.id} {...stylex.props(styles.th)}>
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} {...stylex.props(styles.td)}>
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!list.isPending && data.length === 0 && (
          <p {...stylex.props(styles.empty)}>No matching recordings within the retention window.</p>
        )}
        {list.isPending && <p {...stylex.props(styles.empty)}>Loading recordings…</p>}
        <Pagination page={currentPage} total={data.length} onChange={setPage} />
        <p {...stylex.props(styles.small)}>
          Up to 200 recent recordings. Replay and downloads are audited. Forwarded protocols and
          SFTP include activity metadata.
        </p>
      </section>
    </>
  );
}
